export const isTauriEnvironment = () =>
    typeof window !== 'undefined' && ('__TAURI__' in window || '__TAURI_INTERNALS__' in window);

// macOS consumes Cmd+period before the WebView receives a DOM keydown.
// The native menu forwards it to the same dismissal path as the web shortcut.
export const registerDesktopDismissMessagePanelListener = handler => {
    if (!isTauriEnvironment()) return undefined;

    let disposed = false;
    let unlisten;
    import('@tauri-apps/api/event')
        .then(({ listen }) => listen('desktop-dismiss-message-panel', () => {
            if (!disposed) handler();
        }))
        .then(removeListener => {
            if (disposed) removeListener();
            else unlisten = removeListener;
        })
        .catch(() => {
            console.warn('[desktop] Could not register message panel dismissal listener');
        });

    return () => {
        disposed = true;
        unlisten?.();
    };
};

// Read the persisted desktop settings (Rust `get_settings` command).
// Returns null outside the Tauri desktop app.
export const getDesktopSettings = async () => {
    if (!isTauriEnvironment()) return null;
    try {
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke('get_settings');
    } catch (error) {
        console.warn('getDesktopSettings failed:', error);
        return null;
    }
};

// Toggle the system tray icon (Rust `set_show_tray` command). Returns the
// updated settings, or null outside the desktop app / on failure.
export const setShowTray = async show => {
    if (!isTauriEnvironment()) return null;
    try {
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke('set_show_tray', { show });
    } catch (error) {
        console.warn('setShowTray failed:', error);
        return null;
    }
};

export const openExternalUrl = async url => {
    const openInBrowser = () => window.open(url, '_blank', 'noopener,noreferrer');

    if (!isTauriEnvironment()) {
        openInBrowser();
        return;
    }

    try {
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        await openUrl(url);
    } catch (error) {
        console.warn('Tauri opener failed, using window.open fallback:', error);
        openInBrowser();
    }
};
